import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, UI } from '..';
import FileUtils from '@gik/core/utils/FileUtils';
import ImageCropper, { IImageCropData } from '../ImageCropper';
import noop from '@gik/core/utils/noop';
import ImageUtils from '@gik/core/utils/ImageUtils';

export interface IImagePickerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  // this can be a base64 encoded image that will be used as an initially
  value?: string;
  maxSize?: number;
  dialogTitle?: string;
  removeButtonText?: string;
  addButtonText?: string;
  noPreview?: boolean;

  onChange?(croppedImage: string): void;
}

export interface IImagePickerRef {
  remove: () => void;
}

export default React.forwardRef<IImagePickerRef, IImagePickerProps>(function ImagePicker(
  {
    className,
    dialogTitle = 'Crop Image',
    removeButtonText = 'Remove Photo',
    addButtonText = 'Choose Photo',
    maxSize = 1024,
    value,
    noPreview = false,
    onChange = noop,
  }: IImagePickerProps,
  ref
) {
  const bem = bemBlock('image-picker');

  const [croppedImage, setCroppedImage] = React.useState<string>(value);

  async function handleSave(imageCropData: IImageCropData) {
    const croppedImage = await ImageUtils.getCroppedImage(
      imageCropData.image,
      imageCropData.croppedAreaPixels,
      imageCropData.rotation
    );
    setCroppedImage(croppedImage);
    onChange(croppedImage);
  }

  function remove() {
    setCroppedImage(null);
    onChange(null);
  }

  async function handlePickerImage() {
    const attachImage = await FileUtils.attachImage(maxSize);
    let dialogImageData: IImageCropData;
    let close: Function;
    const response: IImageCropData = await UI.dialog(
      props => {
        close = props.close;
        return (
          <div className={bem('image-upload-dialog-content')}>
            <ImageCropper
              image={attachImage}
              fallback={<div>No Image</div>}
              onChange={image => {
                dialogImageData = image;
              }}
            />
          </div>
        );
      },
      {
        title: dialogTitle,
        buttons: (
          <div>
            <Button onClick={() => close(dialogImageData)}>Save</Button>
          </div>
        ),
      }
    );

    handleSave(response);
  }

  React.useImperativeHandle(ref, () => ({
    remove,
  }));

  return (
    <div className={bem(null, null, className)}>
      {!croppedImage && (
        <Button className={bem('button', [{ empty: true }])} onClick={handlePickerImage}>
          {addButtonText}
        </Button>
      )}
      {croppedImage && (
        <>
          <Button className={bem('button', [{ selected: true }])} onClick={remove}>
            {removeButtonText}
          </Button>
          {!noPreview && (
            <>
              <br />
              <img src={croppedImage} className={bem('image')} />
            </>
          )}
        </>
      )}
    </div>
  );
});
